import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  Surface,
  cardStyle,
  eyebrowStyle,
  ghostButtonStyle,
  ghostLinkStyle,
  inputStyle,
  pageStyle,
  primaryButtonStyle,
} from '@kilat/ui';
import { Link } from 'react-router-dom';
import { Timestamp } from 'firebase/firestore';

import { useAuth } from '../lib/use-auth.js';
import { useI18n } from '../lib/use-i18n.js';
import { useActiveOutlet } from '../lib/use-active-outlet.js';
import { useTrackedSnapshot } from '../lib/use-tracked-snapshot.js';
import { promotionsRepoFor } from '../lib/repositories/promotions-repo.js';
import { assessPromotionMargin, canPromotionEverFire } from '@kilat/shared';
import {
  promotionApplicationsRepoFor,
  ticketsHistoryRepoFor,
  vehicleTypesRepoFor,
} from '../lib/repositories/outlet-repos.js';
import { LOYALTY_CONFIG_DOC_ID, loyaltyConfigRepoFor } from '../lib/repositories/loyalty-repo.js';
import { logger } from '../lib/logger.js';
import { PAGE_FRAME_MAX, dashboardPageStyle, sectionGridStyle } from '../lib/layout.js';
import { callAiDraftPromotion, type AiPromotionDraft } from '../lib/callables/ai.js';
import {
  money,
  parseRateLimitError,
  resolvePromotions,
  type LoyaltyConfig,
  type Promotion,
  type PromotionApplication,
  type PromotionCondition,
  type PromotionEffect,
  type Ticket,
  type VehicleType,
} from '@kilat/shared';

/** Per-promotion roll-up used by the list to render redemption stats. */
interface PromoStats {
  readonly redemptionCount: number;
  readonly totalSavedSen: number;
  readonly lastRedeemedAtMs: number | null;
}

/**
 * /loyalty/promotions — operator-authored auto-applied promotions.
 *
 * Slice (Sprint 127 — promotions builder): adds the missing controls the
 * completion-roadmap called out:
 *   1. A tenant-wide KILL SWITCH ("Pause all promotions") that writes
 *      `loyaltyProgram/config.promotionsPaused`. bookTicket + appendItemsToTicket
 *      read it server-side, so a paused tenant genuinely charges full price —
 *      the switch is authoritative, not cosmetic.
 *   2. A real CONDITIONS editor (minimum spend, vehicle-type limit, outlet
 *      limit) — every condition surfaced here is honoured by the resolver, so
 *      an operator never authors a control that silently does nothing.
 *   3. A fixed-amount effect (RM off) alongside the percentage effect — both
 *      are honoured by the resolver.
 *   4. A live PREVIEW that runs the SAME `resolvePromotions` the checkout runs,
 *      so the operator sees the effect on a sample cart before saving.
 *
 * Per [feedback-no-dead-ends]: every surface has explicit
 * loading / empty / error / saving / saved states with a next action.
 * Per [feedback-custom-crafted-ui-text]: all copy via useI18n.
 */

const DOW_IDS: readonly { id: number; key: string }[] = [
  { id: 1, key: 'mon' },
  { id: 2, key: 'tue' },
  { id: 3, key: 'wed' },
  { id: 4, key: 'thu' },
  { id: 5, key: 'fri' },
  { id: 6, key: 'sat' },
  { id: 0, key: 'sun' },
];

const NS = 'dashboard.loyaltyPromotions';

/** Decorative sparkle glyph for the AI surfaces — not translatable copy. */
const SPARKLE = '✦';

export function LoyaltyPromotionsPage() {
  const { t } = useI18n();
  const { user, claims, loading: authLoading } = useAuth();
  const tenantId =
    typeof claims?.['defaultTenant'] === 'string' ? (claims['defaultTenant'] as string) : null;
  const { outlets, loading: outletsLoading } = useActiveOutlet();
  const [showForm, setShowForm] = useState(false);
  const [showAiDraft, setShowAiDraft] = useState(false);

  const subscribe = useMemo(() => {
    if (!tenantId) return null;
    const repo = promotionsRepoFor(tenantId);
    return (cb: (vs: Promotion[]) => void) => repo.listSnapshot(cb);
  }, [tenantId]);

  const {
    data: promotions,
    loading: promosLoading,
    error,
  } = useTrackedSnapshot<Promotion[]>(['promotions', tenantId], subscribe);

  const { statsByPromoId, loading: statsLoading } = usePromotionStats(tenantId, outlets);

  if (authLoading) return <Splash label={t(`${NS}.loading`)} />;
  if (!user) return <Splash label={t(`${NS}.signIn`)} />;
  if (!tenantId) return <Splash label={t(`${NS}.loadingAccount`)} />;

  return (
    <main style={dashboardPageStyle}>
      <Header
        onNew={() => setShowForm((v) => !v)}
        formOpen={showForm}
        onAiDraft={() => setShowAiDraft((v) => !v)}
        aiOpen={showAiDraft}
      />
      <section
        style={{
          maxWidth: PAGE_FRAME_MAX,
          margin: '0 auto',
          padding: '0 var(--space-5) var(--space-12) var(--space-5)',
          display: 'grid',
          gap: 'var(--space-4)',
        }}
      >
        <KillSwitchCard tenantId={tenantId} />
        {showAiDraft ? (
          <AiDraftPanel
            tenantId={tenantId}
            createdBy={user.uid}
            onClose={() => setShowAiDraft(false)}
          />
        ) : null}
        {showForm ? (
          <NewPromotionForm
            tenantId={tenantId}
            createdBy={user.uid}
            onClose={() => setShowForm(false)}
          />
        ) : null}
        {error ? (
          <ErrorCard message={error.message} />
        ) : promosLoading || !promotions ? (
          <SkeletonRows />
        ) : promotions.length === 0 ? (
          <EmptyCard onNew={() => setShowForm(true)} />
        ) : (
          <PromotionList
            promotions={promotions}
            tenantId={tenantId}
            statsByPromoId={statsByPromoId}
            statsLoading={statsLoading || outletsLoading}
          />
        )}
        <RecentApplicationsCard
          tenantId={tenantId}
          outlets={outlets}
          loadingOutlets={outletsLoading}
        />
      </section>
    </main>
  );
}

// ── recent promotion applications (audit trail) ────────────────────────

/** How many recent applications the audit list holds — bounded, newest first. */
const RECENT_APPLICATIONS_LIMIT = 25;

function appliedAtMs(a: PromotionApplication): number {
  const ts = a.appliedAt as { toMillis?: () => number; seconds?: number };
  return ts.toMillis?.() ?? (ts.seconds ?? 0) * 1000;
}

/**
 * Live audit trail of the latest promotions applied across the operator's
 * outlets. Reads `tenants/{tid}/outlets/{oid}/promotionApplications` per
 * outlet as a BOUNDED slice (orderBy `appliedAt` desc, limit N) so a page
 * open costs O(N) reads, never a full scan of a collection that grows with
 * every discounted booking. One listener per outlet, all torn down together.
 */
function useRecentPromotionApplications(
  tenantId: string | null,
  outlets: readonly { readonly id: string }[],
): { applications: PromotionApplication[]; loading: boolean } {
  const outletIds = useMemo(
    () =>
      outlets
        .map((o) => o.id)
        .sort()
        .join(','),
    [outlets],
  );

  const subscribe = useMemo(() => {
    if (!tenantId || outletIds === '') return null;
    const ids = outletIds.split(',').filter(Boolean);
    return (cb: (values: PromotionApplication[]) => void) => {
      const perOutlet = new Map<string, PromotionApplication[]>();
      const emit = () => {
        const merged = Array.from(perOutlet.values()).flat();
        merged.sort((a, b) => appliedAtMs(b) - appliedAtMs(a));
        cb(merged.slice(0, RECENT_APPLICATIONS_LIMIT));
      };
      const unsubs = ids.map((oid) =>
        promotionApplicationsRepoFor(tenantId, oid).listSnapshotBounded(
          { orderBy: [['appliedAt', 'desc']], limitTo: RECENT_APPLICATIONS_LIMIT },
          (rows) => {
            perOutlet.set(oid, rows);
            emit();
          },
          () => {
            // Degrade gracefully — treat this outlet as empty rather than
            // stalling the whole card (no-dead-ends).
            perOutlet.set(oid, []);
            emit();
          },
        ),
      );
      return () => unsubs.forEach((u) => u());
    };
  }, [tenantId, outletIds]);

  const { data, loading } = useTrackedSnapshot<PromotionApplication[]>(
    ['promo-applications', tenantId, outletIds],
    subscribe,
  );
  return { applications: data ?? [], loading };
}

function RecentApplicationsCard({
  tenantId,
  outlets,
  loadingOutlets,
}: {
  readonly tenantId: string;
  readonly outlets: readonly { readonly id: string }[];
  readonly loadingOutlets: boolean;
}) {
  const { t } = useI18n();
  const { applications, loading } = useRecentPromotionApplications(tenantId, outlets);
  const busy = loading || loadingOutlets;

  return (
    <Surface as="article">
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 'var(--space-3)',
          flexWrap: 'wrap',
        }}
      >
        <h2 style={{ margin: 0, fontSize: 'var(--text-body)', fontWeight: 600 }}>
          {t(`${NS}.applications.heading`)}
        </h2>
        <span style={{ ...metaStyle, marginTop: 0 }}>{t(`${NS}.applications.sub`)}</span>
      </div>
      {busy && applications.length === 0 ? (
        <p style={{ ...metaStyle }}>{t(`${NS}.applications.loading`)}</p>
      ) : applications.length === 0 ? (
        <p style={{ ...metaStyle }}>{t(`${NS}.applications.empty`)}</p>
      ) : (
        <ul
          style={{
            listStyle: 'none',
            margin: 'var(--space-3) 0 0 0',
            padding: 0,
            display: 'grid',
            gap: 'var(--space-2)',
          }}
        >
          {applications.map((a) => (
            <li
              key={a.id}
              style={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                gap: 'var(--space-3)',
                flexWrap: 'wrap',
                padding: 'var(--space-2) 0',
                borderTop: '1px solid var(--color-border)',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              <span style={{ fontSize: 'var(--text-meta)', fontWeight: 500 }}>
                {a.promotionName ?? a.promotionId}
                <span style={{ color: 'var(--color-on-surface-muted)', fontWeight: 400 }}>
                  {' '}
                  {t(`${NS}.applications.onTicket`, { number: a.ticketNumber })}
                </span>
              </span>
              <span
                style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}
              >
                <span style={{ color: 'var(--color-primary)', fontWeight: 600 }}>
                  {t(`${NS}.applications.saved`, { amount: money.format(a.savingsSen) })}
                </span>
                {' · '}
                {t(`${NS}.applications.when`, { when: friendlyRelative(appliedAtMs(a), t) })}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Surface>
  );
}

// ── tenant-wide kill switch ────────────────────────────────────────────

/**
 * Master "Pause all promotions" toggle. Writes
 * `tenants/{tid}/loyaltyProgram/config.promotionsPaused`. The server pricing
 * path (bookTicket / appendItemsToTicket) reads this flag and passes it to
 * `resolvePromotions`, so flipping it here genuinely stops every discount —
 * the control is authoritative, not decorative.
 *
 * States (no-dead-ends): loading skeleton, missing-config hint (points the
 * operator at /loyalty/settings), live view, paused view, and a per-action
 * saving/failed state.
 */
function KillSwitchCard({ tenantId }: { readonly tenantId: string }) {
  const { t } = useI18n();
  const repo = useMemo(() => loyaltyConfigRepoFor(tenantId), [tenantId]);
  const subscribe = useMemo(
    () => (cb: (v: LoyaltyConfig | null) => void) => repo.snapshot(LOYALTY_CONFIG_DOC_ID, cb),
    [repo],
  );
  const { data: config, loading } = useTrackedSnapshot<LoyaltyConfig | null>(
    ['loyalty-config-promotions', tenantId],
    subscribe,
  );

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const paused = config?.promotionsPaused === true;

  async function toggle(next: boolean) {
    setBusy(true);
    setError(null);
    try {
      await repo.update(LOYALTY_CONFIG_DOC_ID, { promotionsPaused: next });
      logger.info('promotions master switch changed', { tenantId, promotionsPaused: next });
    } catch (e) {
      setError(t(`${NS}.killSwitch.saveFailed`, { error: (e as Error).message ?? '' }));
    } finally {
      setBusy(false);
    }
  }

  if (loading && !config) {
    return (
      <div
        aria-hidden
        style={{ height: 96, borderRadius: 'var(--radius-lg)', background: 'var(--color-border)' }}
      />
    );
  }

  if (!config) {
    return (
      <Surface as="article">
        <p style={{ margin: 0, fontSize: 'var(--text-body)', fontWeight: 600 }}>
          {t(`${NS}.killSwitch.heading`)}
        </p>
        <p style={{ ...metaStyle, lineHeight: 1.5 }}>{t(`${NS}.killSwitch.noConfig`)}</p>
        <Link
          to="/loyalty/settings"
          style={{ ...ghostLinkStyle, marginTop: 'var(--space-3)', display: 'inline-block' }}
        >
          {t(`${NS}.killSwitch.goToSettings`)}
        </Link>
      </Surface>
    );
  }

  return (
    <article
      style={{
        ...cardStyle,
        border: paused ? '1px solid rgba(245,158,11,0.35)' : '1px solid rgba(13,122,79,0.22)',
        background: paused ? 'rgba(245,158,11,0.05)' : 'var(--color-surface)',
      }}
    >
      <div
        style={{
          display: 'flex',
          gap: 'var(--space-3)',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ flex: 1, minWidth: 220 }}>
          <p
            style={{
              margin: 0,
              fontSize: 'var(--text-body)',
              fontWeight: 600,
              display: 'flex',
              gap: 'var(--space-2)',
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            {t(`${NS}.killSwitch.heading`)}
            <span style={paused ? masterPausedPillStyle : masterLivePillStyle}>
              {paused ? t(`${NS}.killSwitch.pausedBadge`) : t(`${NS}.killSwitch.liveBadge`)}
            </span>
          </p>
          <p style={{ ...metaStyle, lineHeight: 1.5 }}>
            {paused ? t(`${NS}.killSwitch.pausedSub`) : t(`${NS}.killSwitch.liveSub`)}
          </p>
          {error ? (
            <p
              role="alert"
              style={{ ...metaStyle, color: 'var(--color-accent)', marginTop: 'var(--space-2)' }}
            >
              {error}
            </p>
          ) : null}
        </div>
        <button
          type="button"
          disabled={busy}
          onClick={() => void toggle(!paused)}
          style={paused ? primaryButtonStyle : warnButtonStyle}
        >
          {busy
            ? paused
              ? t(`${NS}.killSwitch.resuming`)
              : t(`${NS}.killSwitch.pausing`)
            : paused
              ? t(`${NS}.killSwitch.resumeAction`)
              : t(`${NS}.killSwitch.pauseAction`)}
        </button>
      </div>
    </article>
  );
}

/**
 * Per-promotion redemption stats — aggregated by scanning `ticketsHistory`
 * across all outlets the operator has access to. One-shot per page load (not
 * a snapshot subscription) because promo stats are looked-at, not watched-live,
 * and this avoids fanning out one listener per outlet just to render a side
 * label on each row.
 *
 * Cost: one query per outlet (full-load of ticketsHistory). Acceptable at
 * MVP scale. Phase-2: roll into a `promotions/{id}/stats` aggregation doc
 * maintained by `onTicketCollected` so the page becomes O(promo count).
 */
function usePromotionStats(
  tenantId: string | null,
  outlets: readonly { readonly id: string }[],
): { statsByPromoId: Record<string, PromoStats>; loading: boolean } {
  const [statsByPromoId, setStatsByPromoId] = useState<Record<string, PromoStats>>({});
  const [loading, setLoading] = useState(false);

  const outletIds = useMemo(
    () =>
      outlets
        .map((o) => o.id)
        .sort()
        .join(','),
    [outlets],
  );

  useEffect(() => {
    if (!tenantId || outletIds === '') {
      setStatsByPromoId({});
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const ids = outletIds.split(',').filter(Boolean);
    Promise.all(ids.map((oid) => ticketsHistoryRepoFor(tenantId, oid).list()))
      .then((perOutlet) => {
        if (cancelled) return;
        const acc: Record<string, PromoStats> = {};
        for (const tickets of perOutlet) {
          for (const tk of tickets) {
            attributeTicket(tk, acc);
          }
        }
        setStatsByPromoId(acc);
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        logger.error('promo-stats: load failed', { error: String(e) });
        setStatsByPromoId({});
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, outletIds]);

  return { statsByPromoId, loading };
}

function attributeTicket(tk: Ticket, acc: Record<string, PromoStats>): void {
  const snap = tk.snapshot;
  const ms = tk.updatedAt?.toMillis?.() ?? null;
  // Prefer Sprint-58 per-promo data when present.
  if (snap.appliedPromotions && snap.appliedPromotions.length > 0) {
    for (const ap of snap.appliedPromotions) {
      const prev = acc[ap.id] ?? { redemptionCount: 0, totalSavedSen: 0, lastRedeemedAtMs: null };
      acc[ap.id] = {
        redemptionCount: prev.redemptionCount + 1,
        totalSavedSen: prev.totalSavedSen + ap.savingsSen,
        lastRedeemedAtMs:
          ms !== null && (prev.lastRedeemedAtMs === null || ms > prev.lastRedeemedAtMs)
            ? ms
            : prev.lastRedeemedAtMs,
      };
    }
    return;
  }
  // Sprint-56 back-compat: IDs + combined total. Split evenly so the count
  // is correct and the total saved sums to the snapshot's combined value.
  const ids = snap.appliedPromotionIds ?? [];
  const total = snap.promotionsApplied ?? 0;
  if (ids.length === 0 || total <= 0) return;
  const per = Math.round(total / ids.length);
  for (const id of ids) {
    const prev = acc[id] ?? { redemptionCount: 0, totalSavedSen: 0, lastRedeemedAtMs: null };
    acc[id] = {
      redemptionCount: prev.redemptionCount + 1,
      totalSavedSen: prev.totalSavedSen + per,
      lastRedeemedAtMs:
        ms !== null && (prev.lastRedeemedAtMs === null || ms > prev.lastRedeemedAtMs)
          ? ms
          : prev.lastRedeemedAtMs,
    };
  }
}

/**
 * AI draft panel — operator types intent ("Rainy Day 20% off for 2 days"),
 * Llama returns a structured Promotion draft, operator reviews + publishes.
 *
 * States per [[feedback-no-dead-ends]]: idle · drafting · suggested ·
 * publishing · published · rate_limited / failed / noop / parse_failed.
 */
function AiDraftPanel({
  tenantId,
  createdBy,
  onClose,
}: {
  readonly tenantId: string;
  readonly createdBy: string;
  readonly onClose: () => void;
}) {
  const { t } = useI18n();
  const [intent, setIntent] = useState('');
  const [stage, setStage] = useState<
    | { kind: 'idle' }
    | { kind: 'drafting' }
    | { kind: 'suggested'; draft: AiPromotionDraft; cacheHit: boolean }
    | { kind: 'parse_failed'; rawText: string }
    | { kind: 'noop'; rawText: string }
    | { kind: 'publishing' }
    | { kind: 'published' }
    | { kind: 'failed'; message: string; retryAfterSec?: number }
  >({ kind: 'idle' });

  const exampleIntents: readonly string[] = [
    t(`${NS}.ai.example1`),
    t(`${NS}.ai.example2`),
    t(`${NS}.ai.example3`),
    t(`${NS}.ai.example4`),
  ];

  async function draftIt() {
    const text = intent.trim();
    if (text.length < 4) return;
    setStage({ kind: 'drafting' });
    try {
      const out = await callAiDraftPromotion({ intent: text, tenantId });
      if (out.provider === 'noop') {
        setStage({ kind: 'noop', rawText: out.rawText });
        return;
      }
      if (!out.draft) {
        setStage({ kind: 'parse_failed', rawText: out.rawText });
        return;
      }
      setStage({ kind: 'suggested', draft: out.draft, cacheHit: out.cacheHit });
    } catch (err) {
      const rl = parseRateLimitError(err);
      if (rl) {
        setStage({ kind: 'failed', message: rl.message, retryAfterSec: rl.retryAfterSec });
        return;
      }
      setStage({
        kind: 'failed',
        message: (err as Error).message ?? t(`${NS}.ai.unreachable`),
      });
    }
  }

  async function publish() {
    if (stage.kind !== 'suggested') return;
    const { draft } = stage;
    setStage({ kind: 'publishing' });
    try {
      const now = Timestamp.now();
      const validityTo = new Date();
      validityTo.setDate(validityTo.getDate() + draft.validForDays);
      validityTo.setHours(23, 59, 59, 999);
      const trigger =
        draft.templateKey === 'day_of_week'
          ? {
              kind: 'schedule' as const,
              dows: draft.dows.slice().sort(),
              timezone: 'Asia/Kuala_Lumpur',
            }
          : draft.templateKey === 'happy_hour' && draft.timeFrom && draft.timeTo
            ? {
                kind: 'schedule' as const,
                ...(draft.dows.length > 0 ? { dows: draft.dows.slice().sort() } : {}),
                timeWindow: { from: draft.timeFrom, to: draft.timeTo },
                timezone: 'Asia/Kuala_Lumpur',
              }
            : { kind: 'always' as const };
      const effect =
        draft.effectType === 'pct_off'
          ? { type: 'pct_off' as const, value: draft.effectValue, appliesTo: 'cart' as const }
          : { type: 'fixed_off' as const, value: draft.effectValue, appliesTo: 'cart' as const };
      const id = `promo-${Date.now().toString(36)}`;
      const promotion: Promotion = {
        id,
        name: { en: draft.name } as Promotion['name'],
        description: { en: draft.description } as Promotion['description'],
        status: 'active',
        templateKey: draft.templateKey,
        triggers: [trigger],
        conditions: {},
        effect,
        stacking: { withVouchers: 'bestOnly', withOtherPromotions: 'bestOnly' },
        limits: {},
        validity: { from: now, to: Timestamp.fromDate(validityTo) },
        version: 1,
        createdBy,
        createdAt: now,
        updatedAt: now,
      };
      await promotionsRepoFor(tenantId).set(id, promotion);
      logger.info('ai-drafted promotion published', {
        promotionId: id,
        intent: intent.slice(0, 60),
      });
      setStage({ kind: 'published' });
      setTimeout(() => {
        setStage({ kind: 'idle' });
        setIntent('');
        onClose();
      }, 1100);
    } catch (err) {
      setStage({
        kind: 'failed',
        message: (err as Error).message ?? t(`${NS}.ai.publishFailed`),
      });
    }
  }

  const drafting = stage.kind === 'drafting' || stage.kind === 'publishing';

  return (
    <article style={aiPanelStyle}>
      <header
        style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 'var(--space-3)' }}
      >
        <span
          aria-hidden
          style={{
            display: 'inline-grid',
            placeItems: 'center',
            width: 32,
            height: 32,
            borderRadius: 10,
            background: 'linear-gradient(135deg, #0d7a4f 0%, #0f8659 100%)',
            color: '#ffffff',
          }}
        >
          {SPARKLE}
        </span>
        <div>
          <h2 style={{ margin: 0, fontSize: 'var(--text-body)', fontWeight: 600 }}>
            {t(`${NS}.ai.heading`)}
          </h2>
          <p
            style={{
              margin: '2px 0 0 0',
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
            }}
          >
            {t(`${NS}.ai.sub`)}
          </p>
        </div>
      </header>

      <label style={{ display: 'grid', gap: 6 }}>
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: 'var(--color-on-surface-muted)',
            letterSpacing: 0.3,
          }}
        >
          {t(`${NS}.ai.label`)}
        </span>
        <textarea
          value={intent}
          onChange={(e) => setIntent(e.target.value)}
          placeholder={t(`${NS}.ai.placeholder`)}
          rows={3}
          maxLength={400}
          disabled={drafting}
          style={{
            padding: '10px 12px',
            border: '1px solid var(--color-border-strong)',
            borderRadius: 10,
            fontSize: 14,
            fontFamily: 'inherit',
            resize: 'vertical',
            background: 'var(--color-surface)',
            color: 'var(--color-on-surface)',
          }}
        />
      </label>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
        {exampleIntents.map((s) => (
          <button
            key={s}
            type="button"
            disabled={drafting}
            onClick={() => setIntent(s)}
            style={exampleChipStyle}
          >
            {s}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 'var(--space-3)' }}>
        <button
          type="button"
          onClick={() => void draftIt()}
          disabled={intent.trim().length < 4 || drafting}
          style={{
            ...primaryButtonStyle,
            opacity: intent.trim().length < 4 || drafting ? 0.6 : 1,
          }}
        >
          {stage.kind === 'drafting' ? t(`${NS}.ai.drafting`) : t(`${NS}.ai.suggest`)}
        </button>
        <button type="button" onClick={onClose} style={ghostButtonStyle}>
          {t(`${NS}.close`)}
        </button>
      </div>

      {/* States */}
      {stage.kind === 'suggested' ? (
        <DraftPreview
          draft={stage.draft}
          cacheHit={stage.cacheHit}
          onPublish={() => void publish()}
          onDiscard={() => setStage({ kind: 'idle' })}
        />
      ) : null}
      {stage.kind === 'parse_failed' ? (
        <ParseFailedCard rawText={stage.rawText} onRetry={() => void draftIt()} />
      ) : null}
      {stage.kind === 'noop' ? <NoopCard /> : null}
      {stage.kind === 'publishing' ? (
        <p
          style={{
            margin: 'var(--space-3) 0 0 0',
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          {t(`${NS}.ai.publishing`)}
        </p>
      ) : null}
      {stage.kind === 'published' ? (
        <p
          style={{
            margin: 'var(--space-3) 0 0 0',
            fontSize: 'var(--text-meta)',
            fontWeight: 600,
            color: 'var(--color-primary)',
          }}
        >
          {t(`${NS}.ai.published`)}
        </p>
      ) : null}
      {stage.kind === 'failed' ? (
        <p
          role="alert"
          style={{
            margin: 'var(--space-3) 0 0 0',
            padding: '10px 14px',
            background: 'rgba(239,68,68,0.06)',
            border: '1px solid rgba(239,68,68,0.20)',
            borderRadius: 8,
            color: '#991b1b',
            fontSize: 'var(--text-meta)',
          }}
        >
          {stage.message}
          {stage.retryAfterSec ? (
            <span
              style={{ display: 'block', marginTop: 4, color: 'var(--color-on-surface-muted)' }}
            >
              {t(`${NS}.ai.retryIn`, { n: stage.retryAfterSec })}
            </span>
          ) : null}
        </p>
      ) : null}
    </article>
  );
}

function DraftPreview({
  draft,
  cacheHit,
  onPublish,
  onDiscard,
}: {
  readonly draft: AiPromotionDraft;
  readonly cacheHit: boolean;
  readonly onPublish: () => void;
  readonly onDiscard: () => void;
}) {
  const { t } = useI18n();
  const effectLine =
    draft.effectType === 'pct_off'
      ? t(`${NS}.effect.pctOff`, { value: draft.effectValue })
      : t(`${NS}.effect.fixedOff`, { amount: money.format(draft.effectValue) });
  const triggerLine =
    draft.templateKey === 'happy_hour' && draft.timeFrom && draft.timeTo
      ? draft.dows.length > 0
        ? t(`${NS}.ai.triggerHappyHourDays`, {
            from: draft.timeFrom,
            to: draft.timeTo,
            days: formatDows(draft.dows, t),
          })
        : t(`${NS}.ai.triggerHappyHour`, { from: draft.timeFrom, to: draft.timeTo })
      : draft.templateKey === 'day_of_week' && draft.dows.length > 0
        ? t(`${NS}.ai.triggerDays`, { days: formatDows(draft.dows, t) })
        : t(`${NS}.ai.triggerAlways`);

  return (
    <div
      style={{
        marginTop: 'var(--space-3)',
        padding: 'var(--space-4)',
        borderRadius: 12,
        background: 'rgba(13,122,79,0.04)',
        border: '1px solid rgba(13,122,79,0.20)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
        <h3 style={{ margin: 0, fontSize: 'var(--text-body)', fontWeight: 600 }}>{draft.name}</h3>
        {cacheHit ? (
          <span
            style={{
              fontSize: 9,
              padding: '2px 6px',
              borderRadius: 999,
              background: 'rgba(13,122,79,0.10)',
              color: 'var(--color-primary)',
              fontWeight: 600,
              letterSpacing: 0.3,
            }}
            title={t(`${NS}.ai.cachedTitle`)}
          >
            {t(`${NS}.ai.cached`)}
          </span>
        ) : null}
      </div>
      <p
        style={{
          margin: '0 0 var(--space-3) 0',
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
          lineHeight: 1.5,
        }}
      >
        {draft.description}
      </p>
      <dl
        style={{
          margin: 0,
          display: 'grid',
          gridTemplateColumns: 'auto 1fr',
          rowGap: 4,
          columnGap: 12,
          fontSize: 'var(--text-meta)',
        }}
      >
        <dt style={dlLabel}>{t(`${NS}.ai.fieldEffect`)}</dt>
        <dd style={dlValue}>{effectLine}</dd>
        <dt style={dlLabel}>{t(`${NS}.ai.fieldWhen`)}</dt>
        <dd style={dlValue}>{triggerLine}</dd>
        <dt style={dlLabel}>{t(`${NS}.ai.fieldValid`)}</dt>
        <dd style={dlValue}>{t(`${NS}.ai.validForDays`, { n: draft.validForDays })}</dd>
      </dl>
      <div style={{ display: 'flex', gap: 8, marginTop: 'var(--space-4)' }}>
        <button type="button" onClick={onPublish} style={primaryButtonStyle}>
          {t(`${NS}.ai.publishIt`)}
        </button>
        <button type="button" onClick={onDiscard} style={ghostButtonStyle}>
          {t(`${NS}.ai.tryAnother`)}
        </button>
      </div>
      <p
        style={{
          margin: 'var(--space-3) 0 0 0',
          fontSize: 11,
          color: 'var(--color-on-surface-muted)',
          fontStyle: 'italic',
        }}
      >
        {t(`${NS}.ai.disclaimer`)}
      </p>
    </div>
  );
}

function ParseFailedCard({
  rawText,
  onRetry,
}: {
  readonly rawText: string;
  readonly onRetry: () => void;
}) {
  const { t } = useI18n();
  return (
    <div
      style={{
        marginTop: 'var(--space-3)',
        padding: 'var(--space-3) var(--space-4)',
        borderRadius: 10,
        background: 'rgba(245,158,11,0.06)',
        border: '1px solid rgba(245,158,11,0.22)',
        display: 'grid',
        gap: 6,
      }}
    >
      <p style={{ margin: 0, fontWeight: 600, fontSize: 'var(--text-meta)', color: '#b45309' }}>
        {t(`${NS}.ai.parseFailedTitle`)}
      </p>
      <p
        style={{
          margin: 0,
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
          lineHeight: 1.5,
        }}
      >
        {t(`${NS}.ai.parseFailedBody`)}
      </p>
      <pre
        style={{
          margin: 'var(--space-2) 0 0 0',
          padding: 'var(--space-2) var(--space-3)',
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: 8,
          fontSize: 11,
          fontFamily: 'inherit',
          whiteSpace: 'pre-wrap',
          maxHeight: 160,
          overflow: 'auto',
        }}
      >
        {rawText.trim()}
      </pre>
      <button
        type="button"
        onClick={onRetry}
        style={{ ...ghostButtonStyle, marginTop: 'var(--space-1)', alignSelf: 'flex-start' }}
      >
        {t(`${NS}.ai.tryAgain`)}
      </button>
    </div>
  );
}

function NoopCard() {
  const { t } = useI18n();
  return (
    <div
      style={{
        marginTop: 'var(--space-3)',
        padding: 'var(--space-3) var(--space-4)',
        borderRadius: 10,
        background: 'rgba(245,158,11,0.06)',
        border: '1px solid rgba(245,158,11,0.22)',
      }}
    >
      <p style={{ margin: 0, fontWeight: 600, fontSize: 'var(--text-meta)', color: '#b45309' }}>
        {t(`${NS}.ai.noopTitle`)}
      </p>
      <p
        style={{
          margin: '4px 0 0 0',
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
          lineHeight: 1.5,
        }}
      >
        {t(`${NS}.ai.noopBody`)}
      </p>
    </div>
  );
}

function formatDows(dows: readonly number[], t: (k: string) => string): string {
  return dows
    .slice()
    .sort()
    .map((d) => t(`${NS}.dow.${DOW_IDS.find((x) => x.id === d)?.key ?? 'sun'}`))
    .join(', ');
}

// ── header ─────────────────────────────────────────────────────────────

function Header({
  onNew,
  formOpen,
  onAiDraft,
  aiOpen,
}: {
  readonly onNew: () => void;
  readonly formOpen: boolean;
  readonly onAiDraft: () => void;
  readonly aiOpen: boolean;
}) {
  const { t } = useI18n();
  return (
    <header
      style={{
        background: 'var(--color-surface)',
        borderBottom: '1px solid var(--color-border)',
        marginBottom: 'var(--space-6)',
      }}
    >
      <div
        style={{
          maxWidth: PAGE_FRAME_MAX,
          margin: '0 auto',
          padding: 'var(--space-5)',
          display: 'flex',
          gap: 'var(--space-4)',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <div>
          <p style={eyebrowStyle}>{t(`${NS}.eyebrow`)}</p>
          <h1
            style={{
              margin: 'var(--space-1) 0 0 0',
              fontSize: 'var(--text-h1)',
              fontWeight: 600,
              lineHeight: 1.2,
            }}
          >
            {t(`${NS}.title`)}
          </h1>
        </div>
        <div
          style={{
            marginLeft: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-3)',
            flexWrap: 'wrap',
          }}
        >
          <button
            type="button"
            onClick={onAiDraft}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '10px 16px',
              borderRadius: 'var(--radius-md)',
              border: '1px solid rgba(13,122,79,0.22)',
              background: aiOpen ? 'rgba(13,122,79,0.08)' : 'rgba(13,122,79,0.04)',
              color: 'var(--color-primary)',
              fontWeight: 600,
              fontSize: 'var(--text-meta)',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            <span aria-hidden style={{ fontSize: 14, lineHeight: 1 }}>
              {SPARKLE}
            </span>
            {aiOpen ? t(`${NS}.closeAiDraft`) : t(`${NS}.draftWithAi`)}
          </button>
          <button type="button" onClick={onNew} style={primaryButtonStyle}>
            {formOpen ? t(`${NS}.close`) : t(`${NS}.new`)}
          </button>
          <Link to="/queue" style={ghostLinkStyle}>
            {t(`${NS}.backToQueue`)}
          </Link>
        </div>
      </div>
    </header>
  );
}

// ── promotion list ─────────────────────────────────────────────────────

function PromotionList({
  promotions,
  tenantId,
  statsByPromoId,
  statsLoading,
}: {
  readonly promotions: readonly Promotion[];
  readonly tenantId: string;
  readonly statsByPromoId: Record<string, PromoStats>;
  readonly statsLoading: boolean;
}) {
  const sorted = promotions.slice().sort((a, b) => statusOrder(a.status) - statusOrder(b.status));
  return (
    <div style={sectionGridStyle}>
      {sorted.map((p) => (
        <PromotionRow
          key={p.id}
          promotion={p}
          tenantId={tenantId}
          stats={statsByPromoId[p.id] ?? null}
          statsLoading={statsLoading}
        />
      ))}
    </div>
  );
}

function PromotionRow({
  promotion,
  tenantId,
  stats,
  statsLoading,
}: {
  readonly promotion: Promotion;
  readonly tenantId: string;
  readonly stats: PromoStats | null;
  readonly statsLoading: boolean;
}) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function setStatus(next: Promotion['status']) {
    setBusy(true);
    setError(null);
    try {
      await promotionsRepoFor(tenantId).update(promotion.id, {
        status: next,
        updatedAt: Timestamp.now(),
        version: (promotion.version ?? 0) + 1,
      });
      logger.info('promotion status updated', { promotionId: promotion.id, status: next });
    } catch (e) {
      setError((e as Error).message ?? t(`${NS}.row.updateFailed`));
    } finally {
      setBusy(false);
    }
  }

  // Shared with the resolver, never re-stated here: a local copy of the
  // supported-trigger list would go stale the day `lifecycle` ships and start
  // warning about a trigger that had begun working.
  const canFire = canPromotionEverFire(promotion);

  return (
    <Surface as="article">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 'var(--space-3)',
        }}
      >
        <div style={{ flex: 1 }}>
          <p style={{ margin: 0, fontSize: 'var(--text-body)', fontWeight: 600 }}>
            {promotion.name['en'] ?? promotion.id} <StatusPill status={promotion.status} />
          </p>
          <p style={metaStyle}>
            {summariseTriggers(promotion, t)} · {summariseEffect(promotion, t)} ·{' '}
            {t(`${NS}.row.validity`, {
              from: formatDate(promotion.validity.from),
              to: formatDate(promotion.validity.to),
            })}
          </p>
          {summariseConditions(promotion, t) ? (
            <p style={{ ...metaStyle, color: 'var(--color-on-surface)' }}>
              {summariseConditions(promotion, t)}
            </p>
          ) : null}
          <StatsLine stats={stats} loading={statsLoading} />
          {/* A promotion whose every trigger is unimplemented can never fire —
              `resolvePromotions` drops it and awards nothing. Until 2026-08-26
              nothing said so, and the row below happily offered "Activate": an
              operator could take the seeded first-visit promotion, activate it,
              watch the pill turn Active, and run a campaign that discounted
              precisely zero washes. Per [[feedback-no-dead-ends]] the screen has
              to say which state it is in AND what to do instead. */}
          {!canFire ? (
            <p
              style={{
                margin: 'var(--space-2) 0 0 0',
                color: 'var(--color-on-surface)',
                fontSize: 'var(--text-meta)',
              }}
            >
              {t(`${NS}.row.triggerNotReady`)}
            </p>
          ) : null}
          {error ? (
            <p
              style={{
                margin: 'var(--space-2) 0 0 0',
                color: 'var(--color-accent)',
                fontSize: 'var(--text-meta)',
              }}
            >
              {error}
            </p>
          ) : null}
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          {promotion.status === 'active' ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => setStatus('paused')}
              style={ghostButtonStyle}
            >
              {t(`${NS}.row.pause`)}
            </button>
          ) : (promotion.status === 'paused' || promotion.status === 'draft') && canFire ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => setStatus('active')}
              style={primaryButtonStyle}
            >
              {t(`${NS}.row.activate`)}
            </button>
          ) : null}
          {promotion.status !== 'archived' ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => setStatus('archived')}
              style={ghostButtonStyle}
            >
              {t(`${NS}.row.archive`)}
            </button>
          ) : null}
        </div>
      </div>
    </Surface>
  );
}

function summariseTriggers(
  p: Promotion,
  t: (k: string, v?: Record<string, string | number>) => string,
): string {
  const parts: string[] = [];
  for (const trig of p.triggers) {
    if (trig.kind === 'always') parts.push(t(`${NS}.summary.alwaysOn`));
    else if (trig.kind === 'schedule') {
      const bits: string[] = [];
      if (trig.dows && trig.dows.length > 0) {
        bits.push(formatDows(trig.dows, t));
      }
      if (trig.timeWindow) bits.push(`${trig.timeWindow.from}–${trig.timeWindow.to}`);
      parts.push(bits.length > 0 ? bits.join(' ') : t(`${NS}.summary.scheduleAllTimes`));
    } else if (trig.kind === 'lifecycle') {
      parts.push(t(`${NS}.summary.lifecycle`, { event: trig.event }));
    } else if (trig.kind === 'audience') {
      parts.push(t(`${NS}.summary.audience`));
    } else if (trig.kind === 'manual') {
      parts.push(t(`${NS}.summary.code`, { code: trig.code }));
    }
  }
  return parts.join(' + ');
}

function summariseEffect(
  p: Promotion,
  t: (k: string, v?: Record<string, string | number>) => string,
): string {
  const e = p.effect;
  if (e.type === 'pct_off') return t(`${NS}.summary.pctOffCart`, { value: e.value });
  if (e.type === 'fixed_off') return t(`${NS}.summary.fixedOff`, { amount: money.format(e.value) });
  if (e.type === 'free_service') return t(`${NS}.summary.freeService`);
  if (e.type === 'replace_price')
    return t(`${NS}.summary.replacePrice`, { amount: money.format(e.newPrice) });
  if (e.type === 'bonus_points') return t(`${NS}.summary.bonusPoints`, { points: e.points });
  return t(`${NS}.summary.freeAddOn`);
}

function summariseConditions(
  p: Promotion,
  t: (k: string, v?: Record<string, string | number>) => string,
): string | null {
  const c = p.conditions;
  const bits: string[] = [];
  if (typeof c.minSpend === 'number' && c.minSpend > 0) {
    bits.push(t(`${NS}.summary.minSpend`, { amount: money.format(c.minSpend) }));
  }
  if (c.vehicleTypeIdIn && c.vehicleTypeIdIn.length > 0) {
    bits.push(t(`${NS}.summary.vehicleLimit`, { n: c.vehicleTypeIdIn.length }));
  }
  if (c.outletIdIn && c.outletIdIn.length > 0) {
    bits.push(t(`${NS}.summary.outletLimit`, { n: c.outletIdIn.length }));
  }
  return bits.length > 0 ? bits.join(' · ') : null;
}

// ── new-promotion form ─────────────────────────────────────────────────

type Template = 'day_of_week' | 'happy_hour';
type EffectKind = 'pct_off' | 'fixed_off';

function NewPromotionForm({
  tenantId,
  createdBy,
  onClose,
}: {
  readonly tenantId: string;
  readonly createdBy: string;
  readonly onClose: () => void;
}) {
  const { t } = useI18n();
  const { outlets } = useActiveOutlet();

  const [template, setTemplate] = useState<Template>('day_of_week');
  const [effectKind, setEffectKind] = useState<EffectKind>('pct_off');
  const [name, setName] = useState('');
  const [pctOff, setPctOff] = useState('20');
  const [fixedOffRm, setFixedOffRm] = useState('5');
  const [dows, setDows] = useState<Set<number>>(new Set([2])); // default Tuesday
  const [fromTime, setFromTime] = useState('17:00');
  const [toTime, setToTime] = useState('19:00');
  const [validityFrom, setValidityFrom] = useState(() => toDateInput(new Date()));
  const [validityTo, setValidityTo] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() + 3);
    return toDateInput(d);
  });
  // Conditions (all resolver-honoured):
  const [minSpendRm, setMinSpendRm] = useState('');
  const [vehicleTypeIds, setVehicleTypeIds] = useState<Set<string>>(new Set());
  const [outletIds, setOutletIds] = useState<Set<string>>(new Set());
  const [vehicleTypes, setVehicleTypes] = useState<VehicleType[]>([]);

  const [stage, setStage] = useState<
    { kind: 'idle' } | { kind: 'saving' } | { kind: 'saved' } | { kind: 'failed'; message: string }
  >({ kind: 'idle' });

  // Load the tenant's active vehicle types so the operator can scope the promo
  // by vehicle. Failure is non-fatal — the vehicle-condition block just stays
  // empty (the operator can still author every other field).
  useEffect(() => {
    let cancelled = false;
    vehicleTypesRepoFor(tenantId)
      .list()
      .then((vts) => {
        if (!cancelled) setVehicleTypes(vts.filter((v) => v.active));
      })
      .catch((e) => {
        logger.warn('promotions form: vehicle types load failed', { error: String(e) });
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  const effect = useMemo<PromotionEffect | null>(() => {
    if (effectKind === 'pct_off') {
      const pct = Number.parseInt(pctOff, 10);
      if (!Number.isFinite(pct) || pct < 1 || pct > 100) return null;
      return { type: 'pct_off', value: pct, appliesTo: 'cart' };
    }
    const sen = rmToSen(fixedOffRm);
    if (sen <= 0) return null;
    return { type: 'fixed_off', value: sen, appliesTo: 'cart' };
  }, [effectKind, pctOff, fixedOffRm]);

  /**
   * The floor, from the tenant's live loyalty config. Absent or 0 = guard off.
   */
  const [minMarginPct, setMinMarginPct] = useState(0);
  useEffect(() => {
    loyaltyConfigRepoFor(tenantId)
      .get(LOYALTY_CONFIG_DOC_ID)
      .then((c) => setMinMarginPct(c?.minMarginPct ?? 0))
      .catch(() => {
        // Non-fatal: an unreadable config means the guard stays off rather than
        // blocking an operator from authoring a promotion at all.
      });
  }, [tenantId]);

  /**
   * The cart to judge the discount against: the SMALLEST one the promotion can
   * apply to, because that is where it does the most damage.
   *
   * With a minimum spend that is the minimum. Without one, a fixed RM 10 off
   * can take a RM 10 wash to nothing — so the amount itself is the worst case,
   * and the resulting warning is really telling the operator to set a minimum.
   * For a percentage the retained share is the same at any size, so the nominal
   * value below only has to be non-zero.
   */
  const marginListPriceSen = useMemo(() => {
    const minSpendSen = rmToSen(minSpendRm);
    if (minSpendSen > 0) return minSpendSen;
    if (effect?.type === 'fixed_off') return effect.value;
    return 10_000;
  }, [minSpendRm, effect]);

  const conditions = useMemo<PromotionCondition>(() => {
    const c: PromotionCondition = {};
    const minSpendSen = rmToSen(minSpendRm);
    if (minSpendSen > 0) c.minSpend = minSpendSen;
    if (vehicleTypeIds.size > 0) c.vehicleTypeIdIn = Array.from(vehicleTypeIds);
    if (outletIds.size > 0) c.outletIdIn = Array.from(outletIds);
    return c;
  }, [minSpendRm, vehicleTypeIds, outletIds]);

  const trigger = useMemo(
    () =>
      template === 'day_of_week'
        ? {
            kind: 'schedule' as const,
            dows: Array.from(dows).sort(),
            timezone: 'Asia/Kuala_Lumpur',
          }
        : {
            kind: 'schedule' as const,
            timeWindow: { from: fromTime, to: toTime },
            timezone: 'Asia/Kuala_Lumpur',
          },
    [template, dows, fromTime, toTime],
  );

  // A second press confirms. Reset whenever the effect changes, so
  // acknowledging one discount never silently green-lights a different one.
  const [marginAcknowledged, setMarginAcknowledged] = useState(false);
  useEffect(() => {
    setMarginAcknowledged(false);
  }, [effectKind, pctOff, fixedOffRm]);

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStage({ kind: 'saving' });
    if (!effect) {
      setStage({
        kind: 'failed',
        message:
          effectKind === 'pct_off' ? t(`${NS}.form.invalidPct`) : t(`${NS}.form.invalidFixed`),
      });
      return;
    }
    // Anti-undercut check (G7). Silent when no floor is set, which is the
    // shipped default — a warning firing against a number nobody chose gets
    // dismissed on reflex, and then it is furniture.
    const assessment = assessPromotionMargin(marginListPriceSen, effect, minMarginPct);
    if (assessment.belowFloor && !marginAcknowledged) {
      setStage({
        kind: 'failed',
        message: t(`${NS}.form.belowMargin`, {
          retained: String(assessment.retainedPct),
          floor: String(minMarginPct),
        }),
      });
      // One more press means "yes, I meant it". The operator owns their
      // pricing; what they should not have is an accident.
      setMarginAcknowledged(true);
      return;
    }

    try {
      const now = Timestamp.now();
      const id = `promo-${Date.now().toString(36)}`;
      const promotion: Promotion = {
        id,
        name: { en: name.trim() || defaultName(template, t) } as Promotion['name'],
        status: 'active',
        templateKey: template,
        triggers: [trigger],
        conditions,
        effect,
        stacking: { withVouchers: 'bestOnly', withOtherPromotions: 'bestOnly' },
        limits: {},
        validity: {
          from: Timestamp.fromDate(fromDateInput(validityFrom)),
          to: Timestamp.fromDate(fromDateInput(validityTo, true)),
        },
        version: 1,
        createdBy,
        createdAt: now,
        updatedAt: now,
      };
      await promotionsRepoFor(tenantId).set(id, promotion);
      logger.info('promotion created', { promotionId: id, template, effect: effect.type });
      setStage({ kind: 'saved' });
      setTimeout(() => {
        setStage({ kind: 'idle' });
        onClose();
      }, 900);
    } catch (err) {
      setStage({
        kind: 'failed',
        message: (err as Error).message ?? t(`${NS}.form.saveFailedShort`),
      });
    }
  }

  function toggleDow(d: number) {
    setDows((prev) => {
      const next = new Set(prev);
      if (next.has(d)) next.delete(d);
      else next.add(d);
      return next;
    });
  }

  return (
    <Surface as="section">
      <header style={{ marginBottom: 'var(--space-4)' }}>
        <h2 style={{ margin: 0, fontSize: 'var(--text-body)', fontWeight: 600 }}>
          {t(`${NS}.form.heading`)}
        </h2>
        <p style={metaStyle}>{t(`${NS}.form.sub`)}</p>
      </header>
      <form onSubmit={submit} style={{ display: 'grid', gap: 'var(--space-4)' }}>
        <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
          <legend style={legendStyle}>{t(`${NS}.form.templateLegend`)}</legend>
          <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
            <TemplateRadio
              label={t(`${NS}.form.templateDayLabel`)}
              description={t(`${NS}.form.templateDayDesc`)}
              selected={template === 'day_of_week'}
              onSelect={() => setTemplate('day_of_week')}
            />
            <TemplateRadio
              label={t(`${NS}.form.templateHourLabel`)}
              description={t(`${NS}.form.templateHourDesc`)}
              selected={template === 'happy_hour'}
              onSelect={() => setTemplate('happy_hour')}
            />
          </div>
        </fieldset>

        <Field label={t(`${NS}.form.nameLabel`)}>
          <input
            style={inputStyle}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={defaultName(template, t)}
          />
        </Field>

        {/* Effect type — percentage OR fixed amount (both resolver-honoured) */}
        <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
          <legend style={legendStyle}>{t(`${NS}.form.effectLegend`)}</legend>
          <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            <SegButton
              label={t(`${NS}.form.effectPct`)}
              selected={effectKind === 'pct_off'}
              onSelect={() => setEffectKind('pct_off')}
            />
            <SegButton
              label={t(`${NS}.form.effectFixed`)}
              selected={effectKind === 'fixed_off'}
              onSelect={() => setEffectKind('fixed_off')}
            />
          </div>
        </fieldset>

        {effectKind === 'pct_off' ? (
          <Field label={t(`${NS}.form.pctLabel`)}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                maxWidth: 200,
              }}
            >
              <input
                style={{ ...inputStyle, textAlign: 'right' }}
                inputMode="numeric"
                value={pctOff}
                onChange={(e) => setPctOff(e.target.value.replace(/\D/g, ''))}
              />
              <span
                style={{ fontSize: 'var(--text-body)', color: 'var(--color-on-surface-muted)' }}
              >
                %
              </span>
            </div>
          </Field>
        ) : (
          <Field label={t(`${NS}.form.fixedLabel`)}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                maxWidth: 220,
              }}
            >
              <span
                style={{ fontSize: 'var(--text-body)', color: 'var(--color-on-surface-muted)' }}
              >
                RM
              </span>
              <input
                style={{ ...inputStyle, textAlign: 'right' }}
                inputMode="decimal"
                value={fixedOffRm}
                onChange={(e) => setFixedOffRm(e.target.value.replace(/[^\d.]/g, ''))}
              />
            </div>
          </Field>
        )}

        {template === 'day_of_week' ? (
          <Field label={t(`${NS}.form.daysLabel`)}>
            <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
              {DOW_IDS.map((d) => {
                const selected = dows.has(d.id);
                return (
                  <button
                    key={d.id}
                    type="button"
                    onClick={() => toggleDow(d.id)}
                    style={pillToggleStyle(selected)}
                  >
                    {t(`${NS}.dow.${d.key}`)}
                  </button>
                );
              })}
            </div>
          </Field>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
            <Field label={t(`${NS}.form.startTime`)}>
              <input
                type="time"
                style={inputStyle}
                value={fromTime}
                onChange={(e) => setFromTime(e.target.value)}
              />
            </Field>
            <Field label={t(`${NS}.form.endTime`)}>
              <input
                type="time"
                style={inputStyle}
                value={toTime}
                onChange={(e) => setToTime(e.target.value)}
              />
            </Field>
          </div>
        )}

        {/* Conditions — every control here is honoured by the checkout resolver */}
        <fieldset style={fieldsetStyle}>
          <legend style={legendStyle}>{t(`${NS}.form.conditionsLegend`)}</legend>
          <p style={{ ...metaStyle, marginTop: 0 }}>{t(`${NS}.form.conditionsSub`)}</p>

          <Field label={t(`${NS}.form.minSpendLabel`)} hint={t(`${NS}.form.minSpendHint`)}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                maxWidth: 220,
              }}
            >
              <span
                style={{ fontSize: 'var(--text-body)', color: 'var(--color-on-surface-muted)' }}
              >
                RM
              </span>
              <input
                style={{ ...inputStyle, textAlign: 'right' }}
                inputMode="decimal"
                placeholder="0"
                value={minSpendRm}
                onChange={(e) => setMinSpendRm(e.target.value.replace(/[^\d.]/g, ''))}
              />
            </div>
          </Field>

          {vehicleTypes.length > 0 ? (
            <Field label={t(`${NS}.form.vehiclesLabel`)} hint={t(`${NS}.form.vehiclesHint`)}>
              <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                {vehicleTypes.map((vt) => {
                  const selected = vehicleTypeIds.has(vt.id);
                  return (
                    <button
                      key={vt.id}
                      type="button"
                      onClick={() => setVehicleTypeIds((s) => toggleInSet(s, vt.id))}
                      style={pillToggleStyle(selected)}
                    >
                      {vt.name['en'] ?? vt.id}
                    </button>
                  );
                })}
              </div>
            </Field>
          ) : null}

          {outlets.length > 1 ? (
            <Field label={t(`${NS}.form.outletsLabel`)} hint={t(`${NS}.form.outletsHint`)}>
              <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                {outlets.map((o) => {
                  const selected = outletIds.has(o.id);
                  return (
                    <button
                      key={o.id}
                      type="button"
                      onClick={() => setOutletIds((s) => toggleInSet(s, o.id))}
                      style={pillToggleStyle(selected)}
                    >
                      {o.name}
                    </button>
                  );
                })}
              </div>
            </Field>
          ) : null}
        </fieldset>

        {/* Live preview — same resolver the checkout runs */}
        <PreviewCard
          effect={effect}
          conditions={conditions}
          trigger={trigger}
          outlets={outlets}
          vehicleTypes={vehicleTypes}
        />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
          <Field label={t(`${NS}.form.validityFrom`)}>
            <input
              type="date"
              style={inputStyle}
              value={validityFrom}
              onChange={(e) => setValidityFrom(e.target.value)}
            />
          </Field>
          <Field label={t(`${NS}.form.validityTo`)}>
            <input
              type="date"
              style={inputStyle}
              value={validityTo}
              onChange={(e) => setValidityTo(e.target.value)}
            />
          </Field>
        </div>

        <div
          style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', flexWrap: 'wrap' }}
        >
          <button type="submit" disabled={stage.kind === 'saving'} style={primaryButtonStyle}>
            {stage.kind === 'saving' ? t(`${NS}.form.submitSaving`) : t(`${NS}.form.submitCreate`)}
          </button>
          <button type="button" onClick={onClose} style={ghostButtonStyle}>
            {t(`${NS}.form.cancel`)}
          </button>
          {stage.kind === 'saved' ? (
            <span style={{ color: 'var(--color-primary)', fontSize: 'var(--text-meta)' }}>
              {t(`${NS}.form.created`)}
            </span>
          ) : null}
          {stage.kind === 'failed' ? (
            <span
              role="alert"
              style={{ color: 'var(--color-accent)', fontSize: 'var(--text-meta)' }}
            >
              {stage.message}
            </span>
          ) : null}
        </div>
      </form>
    </Surface>
  );
}

/**
 * Live "what this does" preview. Builds a throwaway active promotion from the
 * current form state and runs the SAME `resolvePromotions` the checkout runs
 * against a sample cart at the current time — so the operator sees the exact
 * effect (or why it wouldn't apply right now) before saving.
 */
function PreviewCard({
  effect,
  conditions,
  trigger,
  outlets,
  vehicleTypes,
}: {
  readonly effect: PromotionEffect | null;
  readonly conditions: PromotionCondition;
  readonly trigger: Promotion['triggers'][number];
  readonly outlets: readonly { readonly id: string }[];
  readonly vehicleTypes: readonly VehicleType[];
}) {
  const { t } = useI18n();

  const sampleSubtotal = Math.max(8000, conditions.minSpend ?? 0);
  const previewOutletId =
    (conditions.outletIdIn && conditions.outletIdIn[0]) ?? outlets[0]?.id ?? 'preview-outlet';
  const previewVehicleId =
    (conditions.vehicleTypeIdIn && conditions.vehicleTypeIdIn[0]) ??
    vehicleTypes[0]?.id ??
    'preview-vehicle';

  const result = useMemo(() => {
    if (!effect) return null;
    const now = Timestamp.now();
    const draft: Promotion = {
      id: 'preview',
      name: { en: 'Preview' } as Promotion['name'],
      status: 'active',
      triggers: [trigger],
      conditions,
      effect,
      stacking: { withVouchers: 'bestOnly', withOtherPromotions: 'bestOnly' },
      limits: {},
      validity: {
        from: Timestamp.fromMillis(now.toMillis() - 60_000),
        to: Timestamp.fromMillis(now.toMillis() + 60 * 60_000),
      },
      version: 0,
      createdBy: 'preview',
      createdAt: now,
      updatedAt: now,
    };
    return resolvePromotions({
      promotions: [draft],
      context: {
        nowMs: Date.now(),
        outletId: previewOutletId,
        vehicleTypeId: previewVehicleId,
        cartSubtotalSen: sampleSubtotal,
      },
    });
  }, [effect, conditions, trigger, previewOutletId, previewVehicleId, sampleSubtotal]);

  const savings = result?.totalSavingsSen ?? 0;
  const applies = savings > 0;

  return (
    <div
      style={{
        padding: 'var(--space-4)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid rgba(13,122,79,0.20)',
        background: 'rgba(13,122,79,0.03)',
        display: 'grid',
        gap: 'var(--space-2)',
      }}
    >
      <p style={{ margin: 0, fontSize: 'var(--text-meta)', fontWeight: 600 }}>
        {t(`${NS}.preview.heading`)}
      </p>
      <p style={{ ...metaStyle, marginTop: 0 }}>
        {t(`${NS}.preview.sampleSub`, { amount: money.format(sampleSubtotal) })}
      </p>
      {!effect ? (
        <p style={{ ...metaStyle, marginTop: 0 }}>{t(`${NS}.preview.needEffect`)}</p>
      ) : applies ? (
        <div
          style={{
            display: 'flex',
            gap: 'var(--space-5)',
            flexWrap: 'wrap',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          <div>
            <span style={previewFigureLabelStyle}>{t(`${NS}.preview.savesLabel`)}</span>
            <span style={{ ...previewFigureStyle, color: 'var(--color-primary)' }}>
              {money.format(savings)}
            </span>
          </div>
          <div>
            <span style={previewFigureLabelStyle}>{t(`${NS}.preview.paysLabel`)}</span>
            <span style={previewFigureStyle}>{money.format(sampleSubtotal - savings)}</span>
          </div>
        </div>
      ) : (
        <p style={{ ...metaStyle, marginTop: 0, color: '#b45309' }}>{t(`${NS}.preview.noneNow`)}</p>
      )}
      <p style={{ margin: 0, fontSize: 11, color: 'var(--color-on-surface-muted)' }}>
        {t(`${NS}.preview.engineNote`)}
      </p>
    </div>
  );
}

function defaultName(template: Template, t: (k: string) => string): string {
  return template === 'day_of_week'
    ? t(`${NS}.form.defaultNameDay`)
    : t(`${NS}.form.defaultNameHour`);
}

function TemplateRadio({
  label,
  description,
  selected,
  onSelect,
}: {
  readonly label: string;
  readonly description: string;
  readonly selected: boolean;
  readonly onSelect: () => void;
}) {
  return (
    <label
      style={{
        display: 'grid',
        gridTemplateColumns: 'auto 1fr',
        gap: 'var(--space-2)',
        cursor: 'pointer',
        padding: 'var(--space-3) var(--space-4)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--color-border)',
        background: selected ? 'rgba(13,122,79,0.04)' : 'transparent',
      }}
    >
      <input
        type="radio"
        checked={selected}
        onChange={onSelect}
        style={{ accentColor: 'var(--color-primary)', marginTop: 4 }}
      />
      <div>
        <span style={{ fontWeight: 500 }}>{label}</span>
        <p style={{ ...metaStyle, marginTop: 2 }}>{description}</p>
      </div>
    </label>
  );
}

function SegButton({
  label,
  selected,
  onSelect,
}: {
  readonly label: string;
  readonly selected: boolean;
  readonly onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      style={{
        padding: '10px 16px',
        borderRadius: 'var(--radius-md)',
        border: `1px solid ${selected ? 'var(--color-primary)' : 'var(--color-border-strong)'}`,
        background: selected ? 'rgba(13,122,79,0.06)' : 'transparent',
        color: selected ? 'var(--color-primary)' : 'var(--color-on-surface)',
        fontSize: 'var(--text-meta)',
        fontWeight: 600,
        cursor: 'pointer',
        fontFamily: 'inherit',
      }}
    >
      {label}
    </button>
  );
}

// ── shared primitives ──────────────────────────────────────────────────

function StatsLine({
  stats,
  loading,
}: {
  readonly stats: PromoStats | null;
  readonly loading: boolean;
}) {
  const { t } = useI18n();
  if (loading) {
    return <p style={{ ...metaStyle, marginTop: 'var(--space-2)' }}>{t(`${NS}.stats.loading`)}</p>;
  }
  if (!stats || stats.redemptionCount === 0) {
    return <p style={{ ...metaStyle, marginTop: 'var(--space-2)' }}>{t(`${NS}.stats.none`)}</p>;
  }
  const last = stats.lastRedeemedAtMs ? friendlyRelative(stats.lastRedeemedAtMs, t) : null;
  return (
    <p
      style={{
        ...metaStyle,
        marginTop: 'var(--space-2)',
        color: 'var(--color-primary)',
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {t(`${NS}.stats.summary`, {
        count: stats.redemptionCount,
        amount: money.format(stats.totalSavedSen),
      })}
      {last ? ` · ${t(`${NS}.stats.lastRelative`, { when: last })}` : ''}
    </p>
  );
}

/** "2m ago" / "3h ago" / "5d ago" — short relative for the stats line. */
function friendlyRelative(
  ms: number,
  t: (k: string, v?: Record<string, string | number>) => string,
): string {
  const diff = Math.max(0, Date.now() - ms);
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return t(`${NS}.relative.justNow`);
  const min = Math.floor(sec / 60);
  if (min < 60) return t(`${NS}.relative.minutes`, { n: min });
  const hr = Math.floor(min / 60);
  if (hr < 24) return t(`${NS}.relative.hours`, { n: hr });
  const day = Math.floor(hr / 24);
  if (day < 30) return t(`${NS}.relative.days`, { n: day });
  const mo = Math.floor(day / 30);
  return t(`${NS}.relative.months`, { n: mo });
}

function StatusPill({ status }: { readonly status: Promotion['status'] }) {
  const { t } = useI18n();
  const palette: Record<Promotion['status'], { bg: string; fg: string }> = {
    active: { bg: 'rgba(13,122,79,0.12)', fg: 'var(--color-primary)' },
    draft: { bg: 'var(--color-border)', fg: 'var(--color-on-surface-muted)' },
    paused: { bg: 'rgba(245,158,11,0.12)', fg: '#b45309' },
    archived: { bg: 'var(--color-border)', fg: 'var(--color-on-surface-muted)' },
  };
  const v = palette[status];
  return (
    <span
      style={{
        marginLeft: 'var(--space-2)',
        padding: '2px 8px',
        borderRadius: 'var(--radius-pill)',
        background: v.bg,
        color: v.fg,
        fontSize: '0.6875rem',
        fontWeight: 600,
        letterSpacing: '0.02em',
        verticalAlign: 'middle',
      }}
    >
      {t(`${NS}.status.${status}`)}
    </span>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  readonly label: string;
  readonly hint?: string;
  readonly children: React.ReactNode;
}) {
  return (
    <label style={{ display: 'grid', gap: 'var(--space-2)' }}>
      <span
        style={{
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
          fontWeight: 500,
        }}
      >
        {label}
      </span>
      {children}
      {hint ? (
        <span style={{ fontSize: '0.75rem', color: 'var(--color-on-surface-muted)' }}>{hint}</span>
      ) : null}
    </label>
  );
}

function SkeletonRows() {
  return (
    <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
      {[0, 1].map((i) => (
        <div
          key={i}
          style={{
            height: 80,
            borderRadius: 'var(--radius-lg)',
            background: 'var(--color-border)',
          }}
        />
      ))}
    </div>
  );
}

function ErrorCard({ message }: { readonly message: string }) {
  const { t } = useI18n();
  return (
    <Surface as="article">
      <p style={{ margin: 0, color: 'var(--color-accent)' }}>
        {t(`${NS}.list.loadError`, { message })}
      </p>
    </Surface>
  );
}

function EmptyCard({ onNew }: { readonly onNew: () => void }) {
  const { t } = useI18n();
  return (
    <article
      style={{ ...cardStyle, padding: 'var(--space-8) var(--space-5)', textAlign: 'center' }}
    >
      <h2 style={{ margin: 0, fontSize: 'var(--text-h2)', fontWeight: 600 }}>
        {t(`${NS}.empty.title`)}
      </h2>
      <p
        style={{
          ...metaStyle,
          maxWidth: 480,
          margin: 'var(--space-3) auto 0 auto',
          lineHeight: 1.5,
        }}
      >
        {t(`${NS}.empty.body`)}
      </p>
      <button
        type="button"
        onClick={onNew}
        style={{ ...primaryButtonStyle, marginTop: 'var(--space-5)' }}
      >
        {t(`${NS}.empty.cta`)}
      </button>
    </article>
  );
}

function Splash({ label }: { readonly label: string }) {
  return (
    <main style={pageStyle}>
      <section
        style={{ ...cardStyle, maxWidth: 480, margin: 'var(--space-8) auto', textAlign: 'center' }}
      >
        <p
          style={{
            margin: 0,
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          {label}
        </p>
      </section>
    </main>
  );
}

// ── helpers ────────────────────────────────────────────────────────────

function toggleInSet(prev: Set<string>, id: string): Set<string> {
  const next = new Set(prev);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
}

function rmToSen(rm: string): number {
  const n = Number.parseFloat(rm);
  return Number.isFinite(n) && n > 0 ? Math.round(n * 100) : 0;
}

function statusOrder(s: Promotion['status']): number {
  switch (s) {
    case 'active':
      return 0;
    case 'draft':
      return 1;
    case 'paused':
      return 2;
    case 'archived':
      return 3;
  }
}

function formatDate(ts: Promotion['validity']['from']): string {
  const ms =
    typeof (ts as { toMillis?: () => number }).toMillis === 'function'
      ? (ts as { toMillis: () => number }).toMillis()
      : ((ts as { seconds: number }).seconds ?? 0) * 1000;
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString();
}

function toDateInput(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function fromDateInput(s: string, endOfDay = false): Date {
  const parts = s.split('-').map((x) => Number.parseInt(x, 10));
  const y = parts[0] ?? 2026;
  const m = (parts[1] ?? 1) - 1;
  const d = parts[2] ?? 1;
  return endOfDay ? new Date(y, m, d, 23, 59, 59, 999) : new Date(y, m, d);
}

// ── styles ─────────────────────────────────────────────────────────────

const fieldsetStyle: React.CSSProperties = {
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)',
  padding: 'var(--space-4)',
  margin: 0,
  display: 'grid',
  gap: 'var(--space-4)',
};

const legendStyle: React.CSSProperties = {
  fontSize: 'var(--text-meta)',
  fontWeight: 600,
  padding: '0 var(--space-2)',
};

const warnButtonStyle: React.CSSProperties = {
  padding: '8px 18px',
  borderRadius: 'var(--radius-pill)',
  background: '#b45309',
  color: '#ffffff',
  border: 'none',
  fontWeight: 600,
  fontSize: 'var(--text-meta)',
  cursor: 'pointer',
  minHeight: 'var(--touch-target)',
};

function pillToggleStyle(selected: boolean): React.CSSProperties {
  return {
    padding: '8px 14px',
    borderRadius: 'var(--radius-pill)',
    border: '1px solid var(--color-border)',
    background: selected ? 'var(--color-primary)' : 'transparent',
    color: selected ? 'var(--color-primary-fg)' : 'var(--color-on-surface)',
    fontSize: 'var(--text-meta)',
    fontWeight: 500,
    cursor: 'pointer',
    minHeight: 'var(--touch-target)',
  };
}

const masterLivePillStyle: React.CSSProperties = {
  padding: '2px 8px',
  borderRadius: 'var(--radius-pill)',
  background: 'rgba(13,122,79,0.12)',
  color: 'var(--color-primary)',
  fontSize: '0.6875rem',
  fontWeight: 600,
  letterSpacing: '0.02em',
};

const masterPausedPillStyle: React.CSSProperties = {
  padding: '2px 8px',
  borderRadius: 'var(--radius-pill)',
  background: 'rgba(245,158,11,0.16)',
  color: '#b45309',
  fontSize: '0.6875rem',
  fontWeight: 600,
  letterSpacing: '0.02em',
};

const previewFigureLabelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 11,
  color: 'var(--color-on-surface-muted)',
  fontWeight: 600,
  letterSpacing: 0.3,
  textTransform: 'uppercase',
};

const previewFigureStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 'var(--text-h2)',
  fontWeight: 600,
  marginTop: 2,
};

const metaStyle: React.CSSProperties = {
  margin: 'var(--space-1) 0 0 0',
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
};

const aiPanelStyle: React.CSSProperties = {
  background: 'var(--color-surface)',
  border: '1px solid rgba(13,122,79,0.18)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--space-5)',
  boxShadow: '0 1px 2px var(--tint-subtle), 0 8px 24px rgba(13,122,79,0.06)',
  display: 'grid',
  gap: 'var(--space-3)',
};

const exampleChipStyle: React.CSSProperties = {
  background: 'rgba(13,122,79,0.04)',
  border: '1px solid rgba(13,122,79,0.16)',
  color: 'var(--color-primary)',
  padding: '6px 12px',
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const dlLabel: React.CSSProperties = {
  color: 'var(--color-on-surface-muted)',
  fontWeight: 600,
  fontSize: 11,
  letterSpacing: 0.3,
  textTransform: 'uppercase',
  paddingTop: 2,
};

const dlValue: React.CSSProperties = {
  margin: 0,
  color: 'var(--color-on-surface)',
  fontSize: 'var(--text-meta)',
};
